/**
 * Escape-first mini markdown renderer for the dialog preview. Runs on
 * customer pages, so the load-bearing property is: the WHOLE input is
 * HTML-escaped before any transform, and the only tags in the output are the
 * fixed set emitted below. Subset matches the dialog toolbar: headings,
 * bold, italic, inline code, unordered lists, http(s) links.
 */

function escapeHtml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

function applyEmphasis(text: string): string {
  text = text.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  return text.replace(/\*([^*]+)\*/g, '<em>$1</em>');
}

/** Inline transforms on already-escaped text. Code spans first, so markup
 * inside them is never interpreted; then links, bold, italic.
 *
 * Links are slotted out (like code spans) before the emphasis pass runs, so
 * a `*` inside the href (e.g. `[x](https://ex.com/a*b*c)`) can never be
 * misread as emphasis markup — emphasis is applied to the label only, before
 * the anchor tag is built. Two placeholder namespaces (`\x00c<n>\x00` for
 * code, `\x00l<n>\x00` for links) are resolved link-then-code at the end so
 * a code span nested inside a link label round-trips correctly too. */
function renderInline(escaped: string): string {
  const codeSlots: string[] = [];
  let text = escaped.replace(/`([^`]+)`/g, (_, code: string) => {
    codeSlots.push(`<code>${code}</code>`);
    return `\x00c${codeSlots.length - 1}\x00`;
  });

  // URL chars are already escaped ("&" is "&amp;", quotes are entities), so
  // the href attribute cannot be broken out of. Only http(s) becomes a link.
  const linkSlots: string[] = [];
  text = text.replace(/\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/gi, (_, label: string, url: string) => {
    linkSlots.push(`<a href="${url}">${applyEmphasis(label)}</a>`);
    return `\x00l${linkSlots.length - 1}\x00`;
  });

  text = applyEmphasis(text);

  text = text.replace(
    // biome-ignore lint/suspicious/noControlCharactersInRegex: NUL is the placeholder sentinel precisely because it cannot occur in typed textarea input
    /\x00l(\d+)\x00/g,
    (_, index: string) => linkSlots[parseInt(index, 10)] || '',
  );
  return text.replace(
    // biome-ignore lint/suspicious/noControlCharactersInRegex: NUL is the placeholder sentinel precisely because it cannot occur in typed textarea input
    /\x00c(\d+)\x00/g,
    (_, index: string) => codeSlots[parseInt(index, 10)] || '',
  );
}

export function renderMarkdown(md: string): string {
  const lines = escapeHtml(md).split('\n');
  const blocks: string[] = [];
  let paragraph: string[] = [];
  let list: string[] = [];

  function flushParagraph(): void {
    if (paragraph.length === 0) return;
    blocks.push(`<p>${paragraph.map(renderInline).join('<br>')}</p>`);
    paragraph = [];
  }
  function flushList(): void {
    if (list.length === 0) return;
    blocks.push(`<ul>${list.map((item) => `<li>${renderInline(item)}</li>`).join('')}</ul>`);
    list = [];
  }

  for (const line of lines) {
    const heading = /^(#{1,3}) (.+)$/.exec(line);
    const listItem = /^- (.*)$/.exec(line);
    if (heading?.[1] && heading[2] !== undefined) {
      flushParagraph();
      flushList();
      const level = heading[1].length;
      blocks.push(`<h${level}>${renderInline(heading[2].trim())}</h${level}>`);
    } else if (listItem?.[1] !== undefined) {
      flushParagraph();
      list.push(listItem[1]);
    } else if (line.trim() === '') {
      flushParagraph();
      flushList();
    } else {
      flushList();
      paragraph.push(line);
    }
  }
  flushParagraph();
  flushList();
  return blocks.join('');
}
